import { createContext, useContext, type PropsWithChildren } from "react";
import type { CanvasMode, CanvasTool } from "../domain/types";

export interface CanvasMessages {
  boardLabel: string;
  sceneLabel: string;
  toolbarLabel: string;
  outlineLabel: string;
  showOutline: string;
  hideOutline: string;
  emptyHint: string;
  emptyCanvas: string;
  loadingCanvas: string;
  failedCanvas: string;
  retry: string;
  loadingReference: string;
  unavailableReference: string;
  referenceResolverUnavailable: string;
  missingReference: string;
  loadingImage: string;
  unavailableImage: string;
  imageResolverUnavailable: string;
  missingImageAttachment: string;
  imagePickerUnavailable: string;
  referencePickerUnavailable: string;
  imageRequestFailed: string;
  referenceRequestFailed: string;
  dismissError: string;
  editText: string;
  undo: string;
  redo: string;
  duplicate: string;
  fitToScreen: string;
  group: string;
  ungroup: string;
  bringFront: string;
  sendBack: string;
  alignHorizontal: string;
  alignVertical: string;
  edgeProperties: string;
  edgeLabel: string;
  edgeColor: string;
  edgeWidth: string;
  arrowStart: string;
  arrowEnd: string;
  modeAction: string;
  modePicker: string;
  rotateHandle: string;
  reconnectStart: string;
  reconnectEnd: string;
  defaultStickyText: string;
  defaultText: string;
  defaultFrameTitle: string;
  nodesCount(count: number): string;
  nodeLabel(type: string): string;
  edgeAccessibleLabel(id: string): string;
  unknownNode(type: string): string;
  referenceLabel(key: string): string;
  tools: Readonly<Record<CanvasTool, string>>;
  modes: Readonly<Record<CanvasMode, string>>;
  modeDescriptions: Readonly<Record<CanvasMode, string>>;
  modeActions: Readonly<Record<CanvasMode, string>>;
  projectColumns: readonly [string, string, string];
}

export type CanvasLocale = "zh-CN" | "zh" | "en";
type CanvasMessageLocale = Exclude<CanvasLocale, "zh">;

const toolLabelsZh: Readonly<Record<CanvasTool, string>> = {
  select: "选择",
  lasso: "套索",
  hand: "平移",
  sticky: "便签",
  text: "文字",
  rectangle: "矩形",
  ellipse: "椭圆",
  diamond: "菱形",
  arrow: "箭头",
  draw: "自由笔",
  eraser: "橡皮擦",
  image: "图片",
  frame: "Frame",
  reference: "页面或块引用",
};

const toolLabelsEn: Readonly<Record<CanvasTool, string>> = {
  select: "Select",
  lasso: "Lasso",
  hand: "Pan",
  sticky: "Sticky note",
  text: "Text",
  rectangle: "Rectangle",
  ellipse: "Ellipse",
  diamond: "Diamond",
  arrow: "Connector",
  draw: "Freehand",
  eraser: "Eraser",
  image: "Image",
  frame: "Frame",
  reference: "Page or block reference",
};

export const CANVAS_MESSAGES: Readonly<
  Record<CanvasMessageLocale, CanvasMessages>
> = {
  "zh-CN": {
    boardLabel: "知识画板",
    sceneLabel: "画布内容",
    toolbarLabel: "画板工具",
    outlineLabel: "画板大纲",
    showOutline: "显示画板大纲",
    hideOutline: "隐藏画板大纲",
    emptyHint: "选择一种工具开始创建",
    emptyCanvas: "这是一个空画板",
    loadingCanvas: "正在加载画板…",
    failedCanvas: "画板加载失败",
    retry: "重试",
    loadingReference: "加载引用…",
    unavailableReference: "引用不可用",
    referenceResolverUnavailable: "未连接引用解析服务",
    missingReference: "缺少引用目标",
    loadingImage: "加载图片…",
    unavailableImage: "图片不可用",
    imageResolverUnavailable: "未连接图片解析服务",
    missingImageAttachment: "缺少图片附件",
    imagePickerUnavailable: "未连接图片选择服务",
    referencePickerUnavailable: "未连接引用选择服务",
    imageRequestFailed: "无法插入图片",
    referenceRequestFailed: "无法插入引用",
    dismissError: "关闭错误提示",
    editText: "编辑画板文字",
    undo: "撤销",
    redo: "重做",
    duplicate: "创建副本",
    fitToScreen: "适应屏幕",
    group: "分组",
    ungroup: "取消分组",
    bringFront: "置于顶层",
    sendBack: "置于底层",
    alignHorizontal: "水平居中对齐",
    alignVertical: "垂直居中对齐",
    edgeProperties: "连接线属性",
    edgeLabel: "标签",
    edgeColor: "颜色",
    edgeWidth: "线宽",
    arrowStart: "起点箭头",
    arrowEnd: "终点箭头",
    modeAction: "应用模式行为",
    modePicker: "画板模式",
    rotateHandle: "旋转节点",
    reconnectStart: "重新连接起点",
    reconnectEnd: "重新连接终点",
    defaultStickyText: "新便签",
    defaultText: "文字",
    defaultFrameTitle: "分组框",
    nodesCount: (count) => `${count} 个节点`,
    nodeLabel: (type) => `${type} 节点`,
    edgeAccessibleLabel: (id) => `连接线 ${id}`,
    unknownNode: (type) => `未知节点：${type}`,
    referenceLabel: (key) => `引用 ${key}`,
    tools: toolLabelsZh,
    modes: {
      freeform: "自由画板",
      mindmap: "思维导图",
      research: "研究",
      project: "项目",
    },
    modeDescriptions: {
      freeform: "不约束空间结构的通用画板。",
      mindmap: "以连接关系和树状布局组织内容。",
      research: "突出资料引用、批注和证据关系。",
      project: "使用分组框表达阶段或泳道。",
    },
    modeActions: {
      freeform: "整理自由画板",
      mindmap: "应用树状布局",
      research: "整理证据关系",
      project: "创建项目分栏",
    },
    projectColumns: ["待处理", "进行中", "已完成"],
  },
  en: {
    boardLabel: "Knowledge canvas",
    sceneLabel: "Canvas content",
    toolbarLabel: "Canvas tools",
    outlineLabel: "Canvas outline",
    showOutline: "Show canvas outline",
    hideOutline: "Hide canvas outline",
    emptyHint: "Choose a tool to start creating",
    emptyCanvas: "This canvas is empty",
    loadingCanvas: "Loading canvas…",
    failedCanvas: "Canvas failed to load",
    retry: "Retry",
    loadingReference: "Loading reference…",
    unavailableReference: "Reference unavailable",
    referenceResolverUnavailable: "Reference resolver is not connected",
    missingReference: "Missing reference target",
    loadingImage: "Loading image…",
    unavailableImage: "Image unavailable",
    imageResolverUnavailable: "Image resolver is not connected",
    missingImageAttachment: "Missing image attachment",
    imagePickerUnavailable: "Image picker is not connected",
    referencePickerUnavailable: "Reference picker is not connected",
    imageRequestFailed: "Could not insert image",
    referenceRequestFailed: "Could not insert reference",
    dismissError: "Dismiss error",
    editText: "Edit canvas text",
    undo: "Undo",
    redo: "Redo",
    duplicate: "Duplicate",
    fitToScreen: "Fit to screen",
    group: "Group",
    ungroup: "Ungroup",
    bringFront: "Bring to front",
    sendBack: "Send to back",
    alignHorizontal: "Align horizontal centers",
    alignVertical: "Align vertical centers",
    edgeProperties: "Connector properties",
    edgeLabel: "Label",
    edgeColor: "Color",
    edgeWidth: "Width",
    arrowStart: "Start arrow",
    arrowEnd: "End arrow",
    modeAction: "Apply mode behavior",
    modePicker: "Canvas mode",
    rotateHandle: "Rotate node",
    reconnectStart: "Reconnect start",
    reconnectEnd: "Reconnect end",
    defaultStickyText: "New note",
    defaultText: "Text",
    defaultFrameTitle: "Frame",
    nodesCount: (count) => `${count} nodes`,
    nodeLabel: (type) => `${type} node`,
    edgeAccessibleLabel: (id) => `Connector ${id}`,
    unknownNode: (type) => `Unknown node: ${type}`,
    referenceLabel: (key) => `Reference ${key}`,
    tools: toolLabelsEn,
    modes: {
      freeform: "Freeform",
      mindmap: "Mind map",
      research: "Research",
      project: "Project",
    },
    modeDescriptions: {
      freeform: "A general canvas without spatial constraints.",
      mindmap: "Organize content as connected tree branches.",
      research: "Emphasize sources, annotations, and evidence links.",
      project: "Use frames to represent stages or swimlanes.",
    },
    modeActions: {
      freeform: "Arrange freeform canvas",
      mindmap: "Apply tree layout",
      research: "Organize evidence links",
      project: "Create project columns",
    },
    projectColumns: ["To do", "In progress", "Done"],
  },
};

interface CanvasI18nContextValue {
  locale: CanvasLocale;
  messages: CanvasMessages;
}

const CanvasI18nContext = createContext<CanvasI18nContextValue>({
  locale: "zh-CN",
  messages: CANVAS_MESSAGES["zh-CN"],
});

export function resolveCanvasMessages(
  locale: CanvasLocale = "zh-CN",
  override: Partial<CanvasMessages> = {},
): CanvasMessages {
  const messageLocale = locale === "zh" ? "zh-CN" : locale;
  return { ...CANVAS_MESSAGES[messageLocale], ...override };
}

export function CanvasI18nProvider({
  locale = "zh-CN",
  messages,
  children,
}: PropsWithChildren<{ locale?: CanvasLocale; messages: CanvasMessages }>) {
  return (
    <CanvasI18nContext.Provider value={{ locale, messages }}>
      {children}
    </CanvasI18nContext.Provider>
  );
}

export function useCanvasMessages(): CanvasMessages {
  return useContext(CanvasI18nContext).messages;
}

export function useCanvasLocale(): CanvasLocale {
  return useContext(CanvasI18nContext).locale;
}
